import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { auditApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill } from "@/components/Atoms";
import { Search, X, AlertCircle, RefreshCcw, Zap, ZapOff } from "lucide-react";

// One entry = one server-side mutation or auth event. See the contract in
// /app/docs/openapi.yaml (schema AuditEntry).
//
// Optimistic fields we render even if the backend doesn't emit them:
//   at (ISO timestamp), username, action, resource, resource_id, detail

const ACTIONS = ["", "create", "update", "delete", "login", "logout"];
const RESOURCES = ["", "user", "client", "contact", "device", "gateway", "callback", "mqtt_topic", "envelope_route", "pipeline", "output"];

// Poll every 5s while the tab is visible. See
// /app/docs/BACKEND-ACTION-ITEMS.md § 3 for the SSE upgrade contract we'd
// swap this out for once the backend exposes /api/audit-log/stream.
const LIVE_POLL_MS = 5000;

// Compact colour palette for the action pill so scanning the log at a glance
// is easy without adding icons for every action.
const ACTION_COLOR = {
  create:  "bg-lime-100 text-lime-700",
  update:  "bg-blue-100 text-blue-800",
  delete:  "bg-red-100 text-red-800",
  login:   "bg-violet-100 text-violet-800",
  logout:  "bg-zinc-200 text-zinc-800",
};

export default function AuditLog() {
  const [filters, setFilters] = useState({ username: "", resource: "", action: "" });
  const [live, setLive] = useState(true);
  const [previousTopId, setPreviousTopId] = useState(null);
  const [flashCount, setFlashCount] = useState(0);

  // Client-side paging (fetch a big page, page in memory) is fine while the
  // list is small. When servers grow, wire this into the API `limit`/`offset`.
  const q = useQuery({
    queryKey: ["audit-log", filters],
    queryFn: () => auditApi.list({ limit: 500, ...filters }),
    retry: 0,
    refetchInterval: live ? LIVE_POLL_MS : false,
    refetchIntervalInBackground: false,
  });

  const entries = React.useMemo(() => {
    const raw = q.data;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.entries)) return raw.entries;
    return [];
  }, [q.data]);

  // Flash-highlight the top rows when new entries stream in.
  React.useEffect(() => {
    if (!entries.length) return;
    const topId = entries[0].id;
    if (previousTopId == null) {
      setPreviousTopId(topId);
      return;
    }
    if (topId !== previousTopId) {
      // Count how many entries are new (up to a sensible cap).
      const idx = entries.findIndex((e) => e.id === previousTopId);
      const newCount = idx === -1 ? Math.min(entries.length, 10) : idx;
      setFlashCount(newCount);
      setPreviousTopId(topId);
      // Fade the flash out after 3s.
      const t = setTimeout(() => setFlashCount(0), 3000);
      return () => clearTimeout(t);
    }
  }, [entries, previousTopId]);

  const pager = usePager(entries, { defaultSize: 50, sizes: [25, 50, 100] });

  // 404 = endpoint not implemented yet on this backend. We surface a helpful
  // hint rather than an angry error banner.
  const notImplemented = q.isError && q.error?.status === 404;

  const clearFilters = () => setFilters({ username: "", resource: "", action: "" });
  const hasFilter = filters.username || filters.resource || filters.action;

  return (
    <PageContainer>
      <PageHeader
        title="Audit log"
        subtitle="Every mutation and auth event, ordered newest first."
        actions={
          <div className="inline-flex items-center gap-2">
            <button
              data-testid="audit-live-toggle"
              onClick={() => setLive((v) => !v)}
              title={live ? "Live streaming on — click to pause" : "Live streaming paused — click to resume"}
              className={`border rounded-md px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition ${
                live
                  ? "border-lime-300 bg-lime-50 text-lime-700 hover:bg-lime-100"
                  : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {live ? (
                <>
                  <span className="relative flex w-2 h-2">
                    <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-lime-500 opacity-60" />
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-lime-500" />
                  </span>
                  <Zap className="w-3.5 h-3.5" /> Live
                </>
              ) : (
                <>
                  <ZapOff className="w-3.5 h-3.5" /> Paused
                </>
              )}
            </button>
            <button
              data-testid="audit-refresh"
              onClick={() => q.refetch()}
              className="border border-zinc-200 bg-white rounded-md px-3 py-2 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5"
            >
              <RefreshCcw className={`w-4 h-4 ${q.isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
          <input
            data-testid="audit-filter-username"
            value={filters.username}
            onChange={(e) => setFilters({ ...filters, username: e.target.value })}
            placeholder="Filter by username…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          />
        </div>
        <select
          data-testid="audit-filter-resource"
          value={filters.resource}
          onChange={(e) => setFilters({ ...filters, resource: e.target.value })}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          {RESOURCES.map((r) => (
            <option key={r} value={r}>{r || "All resources"}</option>
          ))}
        </select>
        <select
          data-testid="audit-filter-action"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          {ACTIONS.map((a) => (
            <option key={a} value={a}>{a || "All actions"}</option>
          ))}
        </select>
        {hasFilter && (
          <button
            data-testid="audit-clear-filters"
            onClick={clearFilters}
            className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 font-mono uppercase tracking-widest"
          >
            <X className="w-3 h-3" /> clear
          </button>
        )}
        <div className="flex-1" />
        <div className="text-xs text-zinc-500 font-mono">
          {q.isLoading ? "loading…" : `${entries.length} entr${entries.length === 1 ? "y" : "ies"}`}
        </div>
      </div>

      {notImplemented ? (
        <div
          data-testid="audit-not-implemented"
          className="border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-6 text-sm"
        >
          <div className="flex items-center gap-2 font-semibold mb-1">
            <AlertCircle className="w-4 h-4" />
            Backend endpoint not implemented yet
          </div>
          <p className="text-amber-800">
            The Go service does not yet expose <code className="font-mono bg-amber-100 px-1 rounded">GET /api/audit-log</code>.
            See <code className="font-mono bg-amber-100 px-1 rounded">/app/docs/BACKEND-AUDIT-LOG-PROMPT.md</code> for
            the contract this page expects, or ping your backend team.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-3 font-semibold w-40">When</th>
                <th className="text-left p-3 font-semibold">User</th>
                <th className="text-left p-3 font-semibold w-24">Action</th>
                <th className="text-left p-3 font-semibold w-32">Resource</th>
                <th className="text-left p-3 font-semibold">Detail</th>
              </tr>
            </thead>
            <tbody data-testid="audit-tbody">
              {pager.pageRows.map((e, i) => {
                // Highlight new rows that arrived via live-poll on page 1.
                const isNew = pager.page === 1 && i < flashCount;
                return (
                <tr
                  key={e.id || `${e.at}-${i}`}
                  className={`border-t border-zinc-100 hover:bg-zinc-50/60 transition-colors ${
                    isNew ? "bg-lime-50 audit-row-flash" : ""
                  }`}
                  data-testid={`audit-row-${i}`}
                >
                  <td className="p-3 font-mono text-xs text-zinc-500 whitespace-nowrap">
                    {formatWhen(e.at)}
                  </td>
                  <td className="p-3">
                    <span className="font-medium">{e.username || "—"}</span>
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest font-mono rounded px-1.5 py-0.5 ${
                        ACTION_COLOR[e.action] || "bg-zinc-100 text-zinc-800"
                      }`}
                    >
                      {e.action || "—"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <CodePill>{e.resource || "—"}</CodePill>
                      {e.resource_id && (
                        <Mono className="text-[10px] text-zinc-500">#{e.resource_id}</Mono>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-zinc-700 truncate max-w-lg" title={e.detail || ""}>
                    {e.detail || <span className="text-zinc-400">—</span>}
                  </td>
                </tr>
                );
              })}
              {pager.total === 0 && !q.isLoading && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-zinc-500">
                    No audit entries match the filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pager {...pager} testidPrefix="audit-pager" />
        </div>
      )}
    </PageContainer>
  );
}

// Timestamps come in ISO — render a readable local-time string with a
// concise date/time layout suited to log scanning.
function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(+d)) return String(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  } catch {
    return String(iso);
  }
}
